import React from 'react';
import { Button } from '@mui/material';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDroppable,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useTranslation } from 'react-i18next';
import { LocationRoomGroup, LocationViewModel, LocationPosition } from '../../models/locations';
import SortablePen, { PenCard } from './PenCell';

type SideKey = 'Left' | 'Right';

interface RoomSchemaProps {
  room: LocationRoomGroup;
  hasWelfareFeature: boolean;
  onPenClick: (id: string) => void;
  onMovePen: (location: LocationViewModel, newPosition: LocationPosition) => void;
  onAddPen: () => void;
}

const sideToPosition: Record<SideKey, LocationPosition> = {
  Left: LocationPosition.Left,
  Right: LocationPosition.Right
};

const Column: React.FC<{ side: SideKey; ids: string[]; children: React.ReactNode }> = ({ side, ids, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: side });

  return (
    <div
      ref={setNodeRef}
      className={`flex-1 flex flex-col gap-3 rounded-lg p-3 min-h-[120px] transition-colors ${
        isOver ? 'bg-blue-50 ring-2 ring-blue-200' : 'bg-gray-50'
      }`}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </div>
  );
};

const RoomSchema: React.FC<RoomSchemaProps> = ({ room, hasWelfareFeature, onPenClick, onMovePen, onAddPen }) => {
  const { t } = useTranslation();

  // Build the id->location lookup and the per-side ordered id lists, preserving backend order.
  const byId = React.useMemo(() => {
    const map: Record<string, LocationViewModel> = {};
    for (const loc of room.locations) {
      map[loc.id] = loc;
    }
    return map;
  }, [room.locations]);

  const buildSides = React.useCallback((): Record<SideKey, string[]> => {
    const sides: Record<SideKey, string[]> = { Left: [], Right: [] };
    for (const loc of room.locations) {
      if (loc.positioning === LocationPosition.Right) {
        sides.Right.push(loc.id);
      } else {
        sides.Left.push(loc.id);
      }
    }
    return sides;
  }, [room.locations]);

  // Signature of the authoritative backend state (ids + side). When it changes we must re-sync
  // `items` in the SAME render as `byId` is recomputed — otherwise a stale id from the previous
  // room would be looked up against the new `byId` map and crash. (Effect-based resync runs too
  // late, leaving one render where items and byId disagree.)
  const signature = room.locations.map((loc) => `${loc.id}:${loc.positioning}`).join('|');

  const [items, setItems] = React.useState<Record<SideKey, string[]>>(buildSides);
  const [syncedSignature, setSyncedSignature] = React.useState(signature);
  const [activeId, setActiveId] = React.useState<string | null>(null);

  if (signature !== syncedSignature) {
    setItems(buildSides());
    setSyncedSignature(signature);
  }

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const findContainer = (id: string): SideKey | undefined => {
    if (id === 'Left' || id === 'Right') return id;
    return (Object.keys(items) as SideKey[]).find((key) => items[key].includes(id));
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeContainer = findContainer(String(active.id));
    const overContainer = findContainer(String(over.id));

    if (!activeContainer || !overContainer || activeContainer === overContainer) return;

    setItems((prev) => {
      const activeItems = prev[activeContainer];
      const overItems = prev[overContainer];

      let overIndex = overItems.indexOf(String(over.id));
      if (overIndex === -1) overIndex = overItems.length;

      return {
        ...prev,
        [activeContainer]: activeItems.filter((id) => id !== String(active.id)),
        [overContainer]: [...overItems.slice(0, overIndex), String(active.id), ...overItems.slice(overIndex)]
      };
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const id = String(active.id);
    const overContainer = findContainer(String(over.id));
    if (!overContainer) return;

    const movedLocation = byId[id];
    const originalSide: SideKey = movedLocation?.positioning === LocationPosition.Right ? 'Right' : 'Left';

    // Only side changes (positioning) can be persisted — there is no stored order field for
    // within-side reordering, so same-side drags simply re-sync to backend order.
    if (overContainer !== originalSide && movedLocation) {
      onMovePen(movedLocation, sideToPosition[overContainer]);
      return;
    }

    // Same side: keep local order responsive but do not persist.
    if (overContainer === originalSide) {
      setItems((prev) => {
        const list = prev[overContainer];
        const oldIndex = list.indexOf(id);
        const newIndex = list.indexOf(String(over.id));
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;
        return { ...prev, [overContainer]: arrayMove(list, oldIndex, newIndex) };
      });
    }
  };

  const renderColumn = (side: SideKey) => {
    const visibleIds = items[side].filter((id) => byId[id]);
    return (
      <Column side={side} ids={visibleIds}>
        {visibleIds.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-xs text-gray-400 italic py-6">
            {t('f-loc-hier-empty-side')}
          </div>
        ) : (
          visibleIds.map((id) => (
            <SortablePen
              key={id}
              location={byId[id]}
              hasWelfareFeature={hasWelfareFeature}
              onClick={() => onPenClick(id)}
            />
          ))
        )}
      </Column>
    );
  };

  const activeLocation = activeId ? byId[activeId] : null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-800">{room.room || t('f-loc-hier-unassigned')}</h3>
          <span className="text-xs text-gray-500">{t('f-loc-hier-pens-count', { count: room.locations.length })}</span>
        </div>
        <Button size="small" color="primary" startIcon={<AddCircleIcon />} onClick={onAddPen}>
          {t('f-loc-hier-add-pen')}
        </Button>
      </div>

      <div className="p-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="flex items-stretch gap-2">
            {renderColumn('Left')}

            {/* Central aisle */}
            <div className="flex flex-col items-center justify-center px-2">
              <div className="w-px flex-1 bg-gray-200" />
              <span className="text-[0.6rem] uppercase tracking-wider text-gray-400 py-2 [writing-mode:vertical-rl] rotate-180">
                {t('f-loc-hier-aisle')}
              </span>
              <div className="w-px flex-1 bg-gray-200" />
            </div>

            {renderColumn('Right')}
          </div>

          <DragOverlay>
            {activeLocation ? (
              <PenCard location={activeLocation} hasWelfareFeature={hasWelfareFeature} isOverlay />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
};

export default RoomSchema;
